import React, { useState } from "react";
import {
    Modal,
    Box,
    TextField,
    Typography,
    FormControlLabel,
    Switch
} from "@mui/material";
import { ButtonOutline, ButtonSolid } from "../styledConstants";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import ApiRequest from "../../utils/ApiRequest";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import config from "../../utils/config";
import TidWhitelistingTable from "./TidWhitelistingTable";
import CsvDownApi from "../ExportFiles/CsvDownApi";
import PdfDown from "../ExportFiles/PdfDown";

const validationSchema = Yup.object().shape({
    tid: Yup.string()
        .matches(/^[A-Za-z0-9]+$/, "Only alphanumeric characters allowed")
        .max(50, "TID must be less than 50 characters")
        .required("TID is required"),

    orgCode: Yup.string()
        .matches(/^[A-Za-z0-9]+$/, "Only alphanumeric characters allowed")
        .max(50, "TID must be less than 50 characters")
        .required("Org Code is required"),
});

function TidWhitelisting() {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [refreshTable, setRefreshTable] = useState(false);

    const handleOpenModal = () => {
        setLimitReached({ tid: false, orgCode: false });
        setIsModalOpen(true);
    };
    const handleCloseModal = () => {
        setLimitReached({ tid: false, orgCode: false });
        setIsModalOpen(false);
    };
    const [limitReached, setLimitReached] = useState({
        tid: false,
        orgCode: false,
    });

    const initialValues = {
        tid: "",
        orgCode: "",
        active: true,
    };

    const headers = [
        { label: "TID", key: "tid" },
        { label: "Org Code", key: "orgCode" },
        { label: "Active", key: "active" },
        { label: "Created At", key: "createdAt" },
        { label: "Updated At", key: "updatedAt" },
    ];

    const pdfHeader = [
        ["TID", "Org Code", "Active", "Created At", "Updated At"]
    ];

    const getTableBody = (data) => {
        return data.map((item) => [
            item.tid,
            item.orgCode,
            item.active ? "Active" : "Inactive",
            item.createdAt,
            item.updatedAt,
        ]);
    };

    const handleSubmit = (values, { resetForm, setErrors }) => {

        const successHandler = (res) => {
            const response = res?.data || res;

            if (response?.status === 400 && response?.fieldErrors) {
                const formattedErrors = {};
                response.fieldErrors.forEach(err => {
                    formattedErrors[err.field] = err.message;
                });

                setErrors(formattedErrors);
                return;
            }

            if (response?.success === false || response?.code !== "0000") {
                toast.error(response?.msg || "Something went wrong");
                return;
            }

            toast.success("TID saved successfully!");
            resetForm();
            handleCloseModal();
            setRefreshTable(prev => !prev);
        };

        const errorHandler = (err) => {
            const fieldErrors = err?.response?.data?.fieldErrors;

            if (fieldErrors && fieldErrors.length > 0) {
                const formattedErrors = {};
                fieldErrors.forEach(error => {
                    formattedErrors[error.field] = error.message;
                });

                setErrors(formattedErrors);
                toast.error(fieldErrors[0]?.message);
                return;
            }

            toast.error(
                err?.response?.data?.message ||
                err?.message ||
                "Failed to save TID details"
            );
        };

        const api = new ApiRequest("", successHandler, errorHandler);

        return api.post(`${config.apis.admin.TID_WHITELISTING}`, values);
    };

    const [downloadPayload, setDownloadPayload] = useState({});

    return (
        <div>
            <ToastContainer position="top-right" autoClose={4000} />

            <div className="flex justify-end mb20" style={{ gap: "10px" }}>
                <CsvDownApi
                    headers={headers}
                    url={`${config.apis.admin.TID_WHITELISTING}search`}
                    params={{
                        tid: "",
                        orgCode: "",
                        isActive: null,
                        createdAt: "",
                        updatedAt: "",
                        page: 0,
                        size: 10000,
                        sortBy: "createdAt",
                        sortDirection: "DESC",
                        isDeleted: false,
                    }}
                    title="CSV"
                    method="post"
                />

                <PdfDown
                    tableHeader={pdfHeader}
                    getTableBody={getTableBody}
                    url={`${config.apis.admin.TID_WHITELISTING}search`}
                    params={{
                        tid: "",
                        orgCode: "",
                        isActive: null,
                        createdAt: "",
                        updatedAt: "",
                        page: 0,
                        size: 10000,
                        sortBy: "createdAt",
                        sortDirection: "DESC",
                        isDeleted: false,
                    }}
                    heading="TID Whitelisting List"
                    fileName="distributor_table"
                    method="post"
                />
            </div>

            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
            >
                <Typography variant="h5" fontWeight="bold" color="white">
                    TID Whitelisting List
                </Typography>

                <ButtonOutline onClick={handleOpenModal} style={{ padding: "8px", background: "#fffffF", width: "10%", fontSize: "15px" }}>
                    Create TID
                </ButtonOutline>
            </Box>

            <Modal open={isModalOpen} onClose={handleCloseModal}>
                <Box
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: 400,
                        bgcolor: "background.paper",
                        boxShadow: 24,
                        p: 3,
                        borderRadius: 2,
                    }}
                >
                    <Typography variant="h6" textAlign="center" mb={1}>
                        TID Whitelisting
                    </Typography>

                    <Formik
                        initialValues={initialValues}
                        validationSchema={validationSchema}
                        onSubmit={handleSubmit}
                    >
                        {({
                            values,
                            errors,
                            touched,
                            handleChange,
                            handleBlur,
                            setFieldValue,
                        }) => (
                            <Form>
                                <TextField
                                    fullWidth
                                    label="TID"
                                    name="tid"
                                    margin="normal"
                                    value={values.tid}
                                    inputProps={{ maxLength: 50 }}
                                    onKeyDown={(e) => {
                                        if (values.tid.length >= 50 && e.key !== "Backspace") {
                                            setLimitReached(prev => ({ ...prev, tid: true }));
                                        }
                                    }}
                                    onChange={(e) => {
                                        handleChange(e);
                                        setLimitReached(prev => ({ ...prev, tid: false }));
                                    }}
                                    onBlur={handleBlur}
                                    error={
                                        (touched.tid && Boolean(errors.tid)) ||
                                        limitReached.tid
                                    }
                                    helperText={
                                        limitReached.tid
                                            ? "Maximum 50 characters reached"
                                            : touched.tid && errors.tid
                                                ? errors.tid
                                                : `${values.tid.length}/50 characters`
                                    }
                                />

                                <TextField
                                    fullWidth
                                    label="Org Code"
                                    name="orgCode"
                                    margin="normal"
                                    value={values.orgCode}
                                    inputProps={{ maxLength: 50 }}
                                    onChange={(e) => {
                                        if (e.target.value.length <= 50) {
                                            handleChange(e);
                                            setLimitReached(prev => ({ ...prev, orgCode: false }));
                                        } else {
                                            setLimitReached(prev => ({ ...prev, orgCode: true }));
                                        }
                                    }}
                                    onKeyDown={(e) => {
                                        if (values.orgCode.length >= 50 && e.key !== "Backspace") {
                                            setLimitReached(prev => ({ ...prev, orgCode: true }));
                                        }
                                    }}
                                    onBlur={handleBlur}
                                    error={
                                        (touched.orgCode && Boolean(errors.orgCode)) ||
                                        limitReached.orgCode
                                    }
                                    helperText={
                                        limitReached.orgCode
                                            ? "Maximum 50 characters reached"
                                            : touched.orgCode && errors.orgCode
                                                ? errors.orgCode
                                                : `${values.orgCode.length}/50 characters`
                                    }
                                />

                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={values.active}
                                            onChange={(e) =>
                                                setFieldValue("active", e.target.checked)
                                            }
                                        />
                                    }
                                    label="Active"
                                />

                                <Box mt={3} display="flex" justifyContent="space-between">
                                    <ButtonOutline onClick={handleCloseModal}
                                        style={{
                                            padding: "6px 14px",
                                            borderRadius: "4px",
                                            border: "1px solid #ccc",
                                            background: "#fff",
                                            cursor: "pointer",
                                        }}>
                                        Cancel
                                    </ButtonOutline>

                                    <ButtonSolid type="submit"
                                        style={{
                                            padding: "6px 14px",
                                            borderRadius: "4px",
                                            border: "none",
                                            background: "#ED141F",
                                            color: "#fff",
                                            cursor: "pointer",
                                        }}>
                                        Save
                                    </ButtonSolid>
                                </Box>
                            </Form>
                        )}
                    </Formik>
                </Box>
            </Modal>
            <TidWhitelistingTable refresh={refreshTable} setDownloadPayload={setDownloadPayload} />
        </div >
    );
}

export default TidWhitelisting;
